// Libs
import { DocumentSnapshot, Timestamp } from "firebase/firestore";
import { z } from "zod";

// Local
import { NotFoundError } from "../error-classes";
import { guardIsPlainObject } from "../utils";

// #region SECTION: Types & Schemas
export type MarketingAttachment = {
  url: string;
  filename: string;
  contentType: string;
  size: number; // File size in bytes
  alt?: string; // Alt text for images
  description?: string; // Optional description

  // Common
  timestampCreated: Timestamp;
  createdBy: string;
  deleted?: boolean;
};

type FirestoreAttributes = {
  id: string;
  refPath: string;
};

export type ExistingMarketingAttachment = MarketingAttachment &
  FirestoreAttributes;

const CommonSchema = z.object({
  filename: z.string().min(1).max(400),
  url: z.string().min(1).max(1000),
  contentType: z.string().min(1).max(100),
  size: z
    .number()
    .min(1)
    .max(50 * 1024 * 1024), // Max 50MB
  alt: z.string().max(200).optional(),
  description: z.string().max(500).optional(),
  createdBy: z.string().min(1).max(200),
  deleted: z.boolean().optional(),
});

const MarketingAttachmentSchema = CommonSchema.extend({
  timestampCreated: z.instanceof(Timestamp),
});

export type MarketingAttachment_CreateAPI = {
  /** ISO datetime string */
  timestampCreated: string;
} & Omit<MarketingAttachment, "timestampCreated">;

const MarketingAttachmentCreateAPISchema = CommonSchema.extend({
  timestampCreated: z.string(),
});

// #region SECTION: Parse Functions

function createMarketingAttachmentFromSnapshot(
  snapshot: DocumentSnapshot,
): ExistingMarketingAttachment {
  const data = snapshot.data();
  if (!data) {
    throw new NotFoundError(
      `Marketing attachment with ID ${snapshot.id} not found`,
    );
  }

  return {
    id: snapshot.id,
    refPath: snapshot.ref.path,
    ...validateMarketingAttachment(data),
  };
}

function validateMarketingAttachment(doc: unknown): MarketingAttachment {
  guardIsPlainObject(doc);
  return MarketingAttachmentSchema.parse(doc);
}

function validateCreateAPI(doc: unknown): MarketingAttachment_CreateAPI {
  guardIsPlainObject(doc);
  return MarketingAttachmentCreateAPISchema.parse(doc);
}

// #region SECTION: Exports

export const MarketingAttachmentHelpers = {
  createMarketingAttachmentFromSnapshot,
  validateMarketingAttachment,
  validateCreateAPI,
};
